import * as React from 'react';
import {
  Checkbox,
  ListItemText,
  MenuItem,
  Select,
  FormControl,
  Divider,
  Button,
  Box,
  InputLabel,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { palette } from 'utils/styles/variables';
import { Clear, ExpandMore } from '@mui/icons-material';

const LoadingIcon = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      pointerEvents: 'none',
    }}
  >
    <CircularProgress
      size={16}
      thickness={5}
      sx={{ color: 'text.secondary', opacity: 0.6 }}
    />
  </Box>
);

export const SelectWithCheckbox: React.FC<{
  filterKey: string;
  label?: string;
  options: { id: string; name: string }[];
  onChange: (key: string, value: string[]) => void;
  loading?: boolean;
}> = ({ filterKey, onChange, label, options, loading = false }) => {
  const [selected, setSelected] = React.useState<string[]>(() =>
    options.map((o) => o.id)
  );
  const [tempSelected, setTempSelected] = React.useState<string[]>(() =>
    options.map((o) => o.id)
  );
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [search, setSearch] = React.useState<string>('');
  const [sortedOptions, setSortedOptions] = React.useState(options);

  const filteredOptions = React.useMemo(() => {
    const lower = search.toLowerCase();
    return sortedOptions.filter((opt) =>
      opt.name.toLowerCase().includes(lower)
    );
  }, [search, sortedOptions]);

  React.useEffect(() => {
    setSelected(options.map((o) => o.id));
    setTempSelected(options.map((o) => o.id));

    setSortedOptions(() => {
      const selectedSet = new Set(selected);
      return [...options].sort((a, b) => {
        const aSelected = selectedSet.has(a.id);
        const bSelected = selectedSet.has(b.id);
        if (aSelected === bSelected) return 0;
        return aSelected ? -1 : 1;
      });
    });
  }, [options]);

  React.useEffect(() => {
    document.body.setAttribute('id', menuOpen ? 'body' : '');
  }, [menuOpen]);

  const allSelected =
    tempSelected.length === options.length && options.length > 0;

  const handleTempChange = (value: string) => {
    if (value === 'All') {
      setTempSelected(allSelected ? [] : options.map((o) => o.id));
    } else {
      const newSelected = tempSelected.includes(value)
        ? tempSelected.filter((v) => v !== value)
        : [...tempSelected, value];
      setTempSelected(newSelected);
    }
  };

  const handleOkClick = () => {
    setSelected(tempSelected);
    onChange(
      filterKey,
      tempSelected.length === options.length ? [] : tempSelected
    );
    setMenuOpen(false);
  };

  const handleClearClick = () => {
    setTempSelected([]);
  };

  const handleOpen = () => {
    if (loading) return;
    setTempSelected(selected);
    setMenuOpen(true);

    // Sort only once when dropdown opens
    // setSortedOptions(() => {
    //   const selectedSet = new Set(selected);
    //   return [...options].sort((a, b) => {
    //     const aSelected = selectedSet.has(a.id);
    //     const bSelected = selectedSet.has(b.id);
    //     if (aSelected === bSelected) return 0;
    //     return aSelected ? -1 : 1;
    //   });
    // });
  };

  const handleClose = () => setMenuOpen(false);

  const renderLabel = () => {
    if (loading) return 'Loading...';
    if (selected.length === options.length) return 'All';
    const labels = options
      .filter(({ id }) => selected.includes(id))
      .map(({ name }) => name);
    return labels.join(', ');
  };

  const isSameSelection = React.useMemo(() => {
    if (tempSelected.length !== selected.length) return false;
    return tempSelected.every((v) => selected.includes(v));
  }, [tempSelected, selected]);

  const disableOk = tempSelected.length === 0 || isSameSelection;
  const disableClear = tempSelected.length === 0;

  return (
    <FormControl
      sx={{
        minWidth: 98,
        width: 98,
        '& .MuiSelect-select': { pb: 0 },
        '& .MuiInput-underline:before': {
          borderBottomColor: palette.primary.main,
        },
        '& .MuiInput-underline:hover:before': {
          borderBottomColor: palette.primary.main,
        },
        '& .MuiInput-underline:after': {
          borderBottomColor: palette.primary.main,
        },
        '& .MuiInput-root': {
          marginTop: '5px',
        },
        '& .MuiInputLabel-root': {
          marginLeft: '-5px',
          fontSize: '13px',
        },
      }}
      size="small"
    >
      <InputLabel shrink id="select-label">
        {label}
      </InputLabel>
      <Select
        multiple
        displayEmpty
        variant="standard"
        labelId="select-label"
        label={label}
        open={menuOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        value={tempSelected}
        renderValue={renderLabel}
        disabled={loading}
        sx={{
          fontSize: '14px',
          opacity: loading ? 0.6 : 1, // subtle fade effect
        }}
        IconComponent={loading ? LoadingIcon : ExpandMore}
        MenuProps={{
          PaperProps: {
            sx: {
              width: 'auto',
              minWidth: '100%',
              maxWidth: 400,
              maxHeight: 320,
              py: 0.5,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
          },
        }}
      >
        {options?.length > 10 ? (
          // 🔍 Search input
          <Box sx={{ px: 1, py: 0.5 }}>
            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                e.stopPropagation();
                setSearch(e.target.value);
              }}
              fullWidth
              variant="outlined"
              sx={{
                '.MuiOutlinedInput-root': { paddingRight: 0.2 },
              }}
              InputProps={{
                sx: { fontSize: '0.75rem', height: 30 },
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearch('');
                      }}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              autoFocus
              onClick={(e) => e.stopPropagation()} // prevent Select from closing
              onKeyDown={(e) => e.stopPropagation()} // 🔥 prevent Select from hijacking typing
            />
          </Box>
        ) : (
          ''
        )}

        {options?.length > 10 && <Divider />}

        {/* All option */}
        <MenuItem
          key="All"
          value="All"
          onClick={() => handleTempChange('All')}
          sx={{ py: 0.25, minHeight: 28 }}
          disableRipple
        >
          <Checkbox
            size="small"
            checked={allSelected}
            sx={{ p: 0.25, mr: 1 }}
          />
          <ListItemText
            primary="All"
            primaryTypographyProps={{ fontSize: '0.8rem' }}
          />
        </MenuItem>

        <Divider sx={{ my: 0.25 }} />

        {/* Regular options */}
        <Box
          sx={{
            maxHeight: options?.length > 10 ? 180 : 200,
            overflowY: 'auto',
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map(({ id, name }) => (
              <MenuItem
                key={id}
                value={id}
                onClick={() => handleTempChange(id)}
                sx={{ py: 0.25, minHeight: 28 }}
              >
                <Checkbox
                  size="small"
                  checked={tempSelected.includes(id)}
                  sx={{ p: 0.25, mr: 1 }}
                />
                <ListItemText
                  primary={name}
                  primaryTypographyProps={{ fontSize: '0.8rem' }}
                />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled sx={{ fontSize: '0.75rem', py: 0.5 }}>
              No results found
            </MenuItem>
          )}
        </Box>

        <Divider sx={{ my: 0.25 }} />

        {/* Footer with Clear + OK buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 1,
            py: 0.5,
          }}
        >
          <Button
            variant="text"
            size="small"
            disabled={disableClear}
            onClick={handleClearClick}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              minHeight: 28,
              px: 1,
              color: disableClear ? 'text.disabled' : palette.primary.main,
            }}
          >
            Clear
          </Button>

          <Button
            variant="contained"
            size="small"
            onClick={handleOkClick}
            disabled={disableOk}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              minHeight: 28,
              px: 0,
              py: 0.25,
              minWidth: 40,
              marginRight: '10px',
            }}
          >
            OK
          </Button>
        </Box>
      </Select>
    </FormControl>
  );
};
